import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Gift, Search, CheckCircle2, AlertTriangle, RefreshCw } from "lucide-react";

export default function GiftCardAdminModule() {
  const [data, setData] = useState(null);
  const [code, setCode] = useState("");
  const [lookup, setLookup] = useState(null);
  const [redeemAmt, setRedeemAmt] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try { const r = await api.get("/gift-card/admin/cards"); setData(r.data); }
    catch { setData({ cards: [], count: 0, total_value: 0, outstanding: 0 }); }
  };
  useEffect(() => { load(); }, []);

  async function validate() {
    const c = code.trim().toUpperCase();
    if (!c) return;
    setBusy(true); setLookup(null);
    try {
      const r = await api.get(`/gift-card/admin/validate/${encodeURIComponent(c)}`);
      setLookup(r.data.card);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Code introuvable");
    }
    setBusy(false);
  }

  async function redeem(full) {
    if (!lookup) return;
    setBusy(true);
    try {
      const body = { code: lookup.code };
      if (!full && redeemAmt) body.amount = Number(redeemAmt);
      const r = await api.post("/gift-card/admin/redeem", body);
      toast.success(`Utilisé : ${r.data.deducted.toFixed(2)} $ · Reste : ${r.data.card.remaining.toFixed(2)} $`);
      setLookup(r.data.card); setRedeemAmt(""); load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec");
    }
    setBusy(false);
  }

  if (!data) return <div className="p-8 text-zinc-400">Chargement…</div>;

  return (
    <div className="p-6 md:p-8 max-w-5xl mx-auto" data-testid="gift-card-admin-module">
      <h1 className="text-2xl font-bold text-zinc-900 flex items-center gap-2"><Gift size={22} /> Cartes-cadeaux</h1>
      <p className="text-sm text-zinc-500 mt-1">Valide un code, vois le montant restant et marque-le comme utilisé (anti double-usage).</p>

      <div className="grid grid-cols-3 gap-4 mt-6">
        <Stat label="Cartes vendues" value={data.count} />
        <Stat label="Valeur totale" value={`${(data.total_value || 0).toFixed(0)} $`} accent="#2A9D8F" />
        <Stat label="Solde à honorer" value={`${(data.outstanding || 0).toFixed(0)} $`} accent="#BF8C00" />
      </div>

      {/* Validation */}
      <div className="mt-6 border border-zinc-200 rounded-xl p-5 bg-white">
        <div className="flex gap-2">
          <div className="flex items-center gap-2 border border-zinc-300 rounded-lg px-3 py-2 flex-1">
            <Search size={16} className="text-zinc-400" />
            <input value={code} onChange={(e) => setCode(e.target.value)} placeholder="IIEM-XXXXXX"
              data-testid="gift-code-input" onKeyDown={(e) => e.key === "Enter" && validate()}
              className="flex-1 outline-none text-sm uppercase tracking-wider bg-transparent" />
          </div>
          <button onClick={validate} disabled={busy} data-testid="gift-validate-btn"
            className="bg-zinc-900 text-white text-sm font-semibold px-5 py-2.5 rounded-lg disabled:opacity-50">Valider</button>
        </div>

        {lookup && (
          <div className="mt-4 border border-zinc-200 rounded-lg p-4 bg-zinc-50" data-testid="gift-lookup-result">
            <div className="flex items-center justify-between">
              <div>
                <div className="font-mono text-lg font-bold text-zinc-900">{lookup.code}</div>
                <div className="text-sm text-zinc-600">{lookup.label}</div>
                <div className="text-xs text-zinc-500 mt-1">Acheté par {lookup.buyer_name || "—"} · pour {lookup.recipient_name || "—"}</div>
              </div>
              <div className="text-right">
                <div className="text-2xl font-bold" style={{ color: lookup.remaining > 0 ? "#2A9D8F" : "#9b2226" }} data-testid="gift-remaining">
                  {lookup.remaining.toFixed(2)} $
                </div>
                <div className="text-xs text-zinc-400">sur {lookup.amount.toFixed(2)} $</div>
              </div>
            </div>
            {lookup.used || lookup.remaining <= 0 ? (
              <div className="mt-3 flex items-center gap-2 text-sm text-[#9b2226] font-semibold">
                <AlertTriangle size={16} /> Carte entièrement utilisée{lookup.used_at ? ` le ${(lookup.used_at || "").slice(0, 10)}` : ""}.
              </div>
            ) : (
              <div className="mt-3 flex items-center gap-2 flex-wrap">
                <input value={redeemAmt} onChange={(e) => setRedeemAmt(e.target.value)} type="number" placeholder="Montant à utiliser (optionnel)"
                  data-testid="gift-redeem-amount" className="border border-zinc-300 rounded-lg px-3 py-2 text-sm w-56" />
                <button onClick={() => redeem(false)} disabled={busy} data-testid="gift-redeem-partial-btn"
                  className="bg-[#BF8C00] text-white text-sm font-semibold px-4 py-2 rounded-lg disabled:opacity-50">Déduire ce montant</button>
                <button onClick={() => redeem(true)} disabled={busy} data-testid="gift-redeem-full-btn"
                  className="bg-[#D90429] text-white text-sm font-semibold px-4 py-2 rounded-lg inline-flex items-center gap-1.5 disabled:opacity-50">
                  <CheckCircle2 size={15} /> Marquer comme entièrement utilisée
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Liste */}
      <div className="mt-6 flex items-center justify-between">
        <h2 className="text-lg font-bold text-zinc-800">Cartes vendues</h2>
        <button onClick={load} className="text-sm inline-flex items-center gap-1.5 text-zinc-500 hover:text-zinc-800"><RefreshCw size={14} /> Actualiser</button>
      </div>
      <div className="mt-2 border border-zinc-200 rounded-lg overflow-hidden bg-white">
        <table className="w-full text-sm" data-testid="gift-cards-list">
          <thead>
            <tr className="text-left text-zinc-500 bg-zinc-50 border-b border-zinc-200">
              <th className="py-3 px-4">Code</th><th className="py-3 px-4">Forfait</th>
              <th className="py-3 px-4">Acheteur</th><th className="py-3 px-4 text-right">Restant</th>
              <th className="py-3 px-4">Statut</th>
            </tr>
          </thead>
          <tbody>
            {data.cards.length === 0 ? (
              <tr><td colSpan={5} className="py-6 text-center text-zinc-400">Aucune carte vendue pour l'instant.</td></tr>
            ) : data.cards.map((c) => (
              <tr key={c.code} className="border-b border-zinc-100" data-testid={`gift-row-${c.code}`}>
                <td className="py-3 px-4 font-mono font-semibold">{c.code}</td>
                <td className="py-3 px-4 text-zinc-600">{c.label}</td>
                <td className="py-3 px-4 text-zinc-600">{c.buyer_name || c.buyer_email || "—"}</td>
                <td className="py-3 px-4 text-right font-semibold">{c.remaining.toFixed(0)} $</td>
                <td className="py-3 px-4">
                  {c.used || c.remaining <= 0
                    ? <span className="text-xs text-[#9b2226] font-semibold">Utilisée</span>
                    : <span className="text-xs text-[#2A9D8F] font-semibold">Active</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div className="border border-zinc-200 rounded-lg p-4 bg-white">
      <div className="text-xs uppercase tracking-wide text-zinc-400">{label}</div>
      <div className="text-2xl font-bold mt-1" style={{ color: accent || "#18181b" }}>{value}</div>
    </div>
  );
}
